import React, { useEffect, useRef } from 'react';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import { useDismissableNotification } from './useDismissableNotification';

export const Duration = {
  XS: 2000,
  S: 2500,
  M: 6000,
  XL: 10000,
};

export interface Props {
  id: number;
  queueName: string;
  cardComponent: React.ComponentType;
  cardProps?: any;
  onOpen?: (id: number, element: HTMLElement) => void;
  onClose?: () => void;
}

function getDuration(isMultiline = false, actionCount = 0) {
  if (!isMultiline) {
    if (!actionCount) {
      return Duration.XS;
    }

    if (actionCount === 1) {
      return Duration.S;
    }

    return Duration.M;
  }

  return Duration.XL;
}

const Notification: React.FC<React.PropsWithChildren<Props>> = ({
  id,
  onOpen: onOpenCallback,
  onClose: onCloseCallback,
  queueName,
  cardComponent: NotificationCard,
  cardProps = {},
  children,
}) => {
  const immortalNotifications = useFlag('yumeruby');
  const customDuration = cardProps.hasOwnProperty('duration');
  const { onClose, onPause, onUnpause } = useDismissableNotification({
    id,
    queueName,
    duration: immortalNotifications
      ? 9999999
      : customDuration
        ? cardProps.duration
        : getDuration(cardProps.multiLine, cardProps.actions && cardProps.actions.length),
    onCloseCallback,
  });

  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    rootRef.current && onOpenCallback && onOpenCallback(id, rootRef.current);
  }, [id, onOpenCallback]);

  return (
    <ErrorBoundary debugComponent="Notifcation">
      <NotificationCard
        {...cardProps}
        rootRef={rootRef}
        onClose={onClose}
        onMouseEnter={onPause}
        onMouseLeave={onUnpause}
      >
        {children}
      </NotificationCard>
    </ErrorBoundary>
  );
};
Notification.displayName = 'Notification';

export default Notification;
